import CacheVersioningItem, { CacheVersioningKeys } from "../models/CacheVersioningItem";
import Country from "../modules/base/models/Country";
import { ChatQuickReplyItem } from "../modules/chat/models/ChatQuickReplyItem ";
import { FlagCompact } from "../modules/crm/base/models/FlagItem";
import { JobMetroCompact } from "../modules/crm/base/models/JobMetro";
import { JobSourceCompact } from "../modules/crm/base/models/JobSource";
import { JobStatusCompact } from "../modules/crm/base/models/JobStatus";
import { JobTypeCompact } from "../modules/crm/base/models/JobType";
import { TagCompact } from "../modules/crm/base/models/TagItem";
import Announcement from "../modules/system/announcements/models/Announcement";
import Technician, { TechCompact } from "../modules/technicians/models/technician";
import { ExtensionCompact } from "../modules/voice/pbx/models/Extension";
import { QueueCompact } from "../modules/voice/pbx/models/Queue";
import { PhoneLineCompact } from "../modules/voice/lines/models/PhoneLine";




export default class Database {

    public static instance: Database = new Database()
    private dbName: string

    private dbVersion = 11

    private cachedDb: IDBDatabase | null = null
    private openPromise: Promise<IDBDatabase> | null = null


    private cacheVersioningStore = "cache_versioning";

    private jobTypes = "job_types";
    private jobSources = "job_sources";
    private jobStatuses = "job_statuses";
    private jobMetros = "job_metors";
    private tags = "tags";
    private flags = "flags";

    private announcements = "announcements";
    private countries = "countries";
    private countriesAll = "countries_all";
    private quickReplies = "quickReplies";

    private technicians = "technicians";

    private extensions = "extensions";
    private queues = "queues";
    private phoneLines = "phone_lines";

    constructor() {
        this.dbName = "rinvox-db"
    }


    private warnedAboutDb = false

    private fallbackToMemoryDb(reason: any): IDBDatabase {
        if (!this.warnedAboutDb) {
            console.warn("[IndexedDB] Local cache unavailable; running with in-memory fallback for this session.", reason)
            this.warnedAboutDb = true
        }
        const mem = createInMemoryDb()
        this.cachedDb = mem
        this.openPromise = null
        return mem
    }

    private open(): Promise<IDBDatabase> {
        if (this.cachedDb) return Promise.resolve(this.cachedDb)
        if (this.openPromise) return this.openPromise

        let request: IDBOpenDBRequest
        try {
            request = indexedDB.open(this.dbName, this.dbVersion)
        } catch (e) {
            return Promise.resolve(this.fallbackToMemoryDb(e))
        }

        this.openPromise = new Promise((resolve) => {

            // indexedDB.open does not always answer. A deleteDatabase still pending behind an
            // open connection, or a storage layer the browser has given up on, leaves the
            // request sitting there with no success, no error and no blocked event -- and the
            // caller awaiting it for ever. That is a dead "App Loading" screen, because the
            // first thing the app does is read this cache and the request that loads the
            // account is never sent. Nothing is worth that, so after a few seconds we stop
            // waiting and run the session from memory.
            let settled = false
            const settle = (db: IDBDatabase) => {
                if (settled) return
                settled = true
                this.openPromise = null
                resolve(db)
            }

            const timeout = window.setTimeout(() => {
                if (settled) return
                settle(this.fallbackToMemoryDb(new Error("IndexedDB did not answer in time.")))
            }, 5000)

            request.onupgradeneeded = () => {
                const db = request.result

                if (!db.objectStoreNames.contains(this.cacheVersioningStore)) {
                    const store = db.createObjectStore(this.cacheVersioningStore, { keyPath: "key" })
                    store.createIndex("key", "key", { unique: true })
                }

                if (!db.objectStoreNames.contains(this.jobStatuses)) {
                    const store = db.createObjectStore(this.jobStatuses, { keyPath: "id" })
                    store.createIndex("id", "id", { unique: true })
                }

                if (!db.objectStoreNames.contains(this.jobTypes)) {
                    const store = db.createObjectStore(this.jobTypes, { keyPath: "id" })
                    store.createIndex("id", "id", { unique: true })
                }

                if (!db.objectStoreNames.contains(this.jobSources)) {
                    const store = db.createObjectStore(this.jobSources, { keyPath: "id" })
                    store.createIndex("id", "id", { unique: true })
                }

                if (!db.objectStoreNames.contains(this.jobMetros)) {
                    const store = db.createObjectStore(this.jobMetros, { keyPath: "id" })
                    store.createIndex("id", "id", { unique: true })
                }

                if (!db.objectStoreNames.contains(this.tags)) {
                    const store = db.createObjectStore(this.tags, { keyPath: "id" })
                    store.createIndex("id", "id", { unique: true })
                }

                if (!db.objectStoreNames.contains(this.flags)) {
                    const store = db.createObjectStore(this.flags, { keyPath: "id" })
                    store.createIndex("id", "id", { unique: true })
                }

                if (!db.objectStoreNames.contains(this.announcements)) {
                    const store = db.createObjectStore(this.announcements, { keyPath: "id" })
                    store.createIndex("id", "id", { unique: true })
                }

                if (!db.objectStoreNames.contains(this.countries)) {
                    const store = db.createObjectStore(this.countries, { keyPath: "id" })
                    store.createIndex("id", "id", { unique: true })
                }

                if (!db.objectStoreNames.contains(this.countriesAll)) {
                    const store = db.createObjectStore(this.countriesAll, { keyPath: "id" })
                    store.createIndex("id", "id", { unique: true })
                }

                if (!db.objectStoreNames.contains(this.quickReplies)) {
                    const store = db.createObjectStore(this.quickReplies, { keyPath: "id" })
                    store.createIndex("id", "id", { unique: true })
                }

                if (!db.objectStoreNames.contains(this.technicians)) {
                    const store = db.createObjectStore(this.technicians, { keyPath: "id" })
                    store.createIndex("id", "id", { unique: true })
                }

                if (!db.objectStoreNames.contains(this.extensions)) {
                    const store = db.createObjectStore(this.extensions, { keyPath: "id" })
                    store.createIndex("id", "id", { unique: true })
                }

                if (!db.objectStoreNames.contains(this.queues)) {
                    const store = db.createObjectStore(this.queues, { keyPath: "id" })
                    store.createIndex("id", "id", { unique: true })
                }

                if (!db.objectStoreNames.contains(this.phoneLines)) {
                    const store = db.createObjectStore(this.phoneLines, { keyPath: "id" })
                    store.createIndex("id", "id", { unique: true })
                }

            }

            request.onsuccess = () => {
                const db = request.result
                window.clearTimeout(timeout)

                // It answered after we had already given up on it. The page is running on the
                // memory cache by now, so leaving this one open would only block the next
                // deleteDatabase; the next page load picks it up properly.
                if (settled) {
                    try { db.close() } catch { }
                    return
                }

                db.onversionchange = () => {
                    db.close()
                    if (this.cachedDb === db) this.cachedDb = null
                }
                db.onclose = () => {
                    if (this.cachedDb === db) this.cachedDb = null
                }

                this.cachedDb = db
                settle(db)
            }
            request.onerror = () => {
                window.clearTimeout(timeout)
                settle(this.fallbackToMemoryDb(request.error))
            }
            request.onblocked = () => {
                window.clearTimeout(timeout)
                settle(this.fallbackToMemoryDb(new Error("Database open blocked by another connection.")))
            }
        })

        return this.openPromise
    }

    private async getDb(): Promise<IDBDatabase> {
        return await this.open()
    }

    private closeCachedDb(): void {
        if (this.cachedDb) {
            try { this.cachedDb.close() } catch { }
            this.cachedDb = null
        }
        this.openPromise = null
    }

    private async clearAllStores(): Promise<void> {
        try {
            const db = await this.open()
            const storeNames = Array.from(db.objectStoreNames)
            if (storeNames.length === 0) return

            await new Promise<void>((resolve, reject) => {
                const tx = db.transaction(storeNames, "readwrite")
                for (const name of storeNames) {
                    try { tx.objectStore(name).clear() } catch { }
                }
                tx.oncomplete = () => resolve()
                tx.onerror = () => reject(tx.error)
                tx.onabort = () => reject(tx.error)
            })
        }
        catch (e) {
            console.warn("clearAllStores failed", e)
        }
    }

    async deleteDatabase(): Promise<void> {
        await this.clearAllStores()
        this.closeCachedDb()

        return await new Promise<void>(resolve => {
            const request = indexedDB.deleteDatabase(this.dbName)
            let settled = false

            const finish = () => {
                if (settled) return
                settled = true
                resolve()
            }

            request.onsuccess = finish
            request.onerror = () => {
                console.warn("IndexedDB deleteDatabase errored", request.error)
                finish()
            }
            request.onblocked = () => {
                console.warn("IndexedDB deletion blocked — data already cleared, continuing.")
                setTimeout(finish, 1500)
            }
        })
    }


    // cache versioning
    async updateCacheVersioning(item: CacheVersioningItem): Promise<void> {
        const db = await this.getDb()

        await new Promise<void>((resolve, reject) => {
            const tx = db.transaction(this.cacheVersioningStore, "readwrite")
            const store = tx.objectStore(this.cacheVersioningStore)

            store.put(item)

            tx.oncomplete = () => resolve()
            tx.onerror = () => reject(tx.error)
            tx.onabort = () => reject(tx.error)
        })
    }

    async updateCacheVersioningList(items: CacheVersioningItem[]): Promise<void> {
        if (!items || items.length === 0) return

        const db = await this.getDb()

        await new Promise<void>((resolve, reject) => {
            const tx = db.transaction(this.cacheVersioningStore, "readwrite")
            const store = tx.objectStore(this.cacheVersioningStore)

            store.clear()

            for (const item of items) {
                store.put(item)
            }

            tx.oncomplete = () => resolve()
            tx.onerror = () => reject(tx.error)
            tx.onabort = () => reject(tx.error)
        })
    }

    async getCacheVersioning(key: CacheVersioningKeys): Promise<CacheVersioningItem | null> {
        const db = await this.getDb()

        return await new Promise<CacheVersioningItem | null>((resolve, reject) => {
            const tx = db.transaction(this.cacheVersioningStore, "readonly")
            const store = tx.objectStore(this.cacheVersioningStore)
            const request = store.get(key)

            request.onsuccess = () => resolve((request.result as CacheVersioningItem) || null)
            request.onerror = () => reject(request.error)
        })
    }

    async getAllCacheVersioning(): Promise<CacheVersioningItem[]> {
        const db = await this.getDb()

        return await new Promise<CacheVersioningItem[]>((resolve, reject) => {
            const tx = db.transaction(this.cacheVersioningStore, "readonly")
            const store = tx.objectStore(this.cacheVersioningStore)
            const request = store.getAll()

            request.onsuccess = () => resolve((request.result || []) as CacheVersioningItem[])
            request.onerror = () => reject(request.error)
        })
    }

    async getRequiredCacheVersioning(onlineItems?: CacheVersioningItem[]): Promise<CacheVersioningItem[]> {
        if (!onlineItems || onlineItems.length === 0) return []

        const db = await this.getDb()

        return await new Promise<CacheVersioningItem[]>((resolve, reject) => {
            const tx = db.transaction(this.cacheVersioningStore, "readonly")
            const store = tx.objectStore(this.cacheVersioningStore)
            const request = store.getAll()

            request.onsuccess = () => {
                const localItems = (request.result || []) as CacheVersioningItem[]

                const localMap = new Map<CacheVersioningKeys, CacheVersioningItem>()
                for (const item of localItems) {
                    localMap.set(item.key, item)
                }

                const requiredItems = onlineItems.filter(onlineItem => {
                    const localItem = localMap.get(onlineItem.key)

                    if (!localItem) return true
                    if (localItem.version !== onlineItem.version) return true

                    return false
                })

                resolve(requiredItems)
            }

            request.onerror = () => reject(request.error)
        })
    }

    async clearCacheVersioning(): Promise<void> {
        const db = await this.getDb()

        await new Promise<void>((resolve, reject) => {
            const tx = db.transaction(this.cacheVersioningStore, "readwrite")
            const store = tx.objectStore(this.cacheVersioningStore)

            store.clear()

            tx.oncomplete = () => resolve()
            tx.onerror = () => reject(tx.error)
            tx.onabort = () => reject(tx.error)
        })
    }

    // job statuses
    async updateJobStatuses(items: JobStatusCompact[]): Promise<void> {
        if (!items || items.length === 0) return
        const db = await this.getDb()
        await new Promise<void>((resolve, reject) => {

            const tx = db.transaction(this.jobStatuses, "readwrite")
            const store = tx.objectStore(this.jobStatuses)

            store.clear()

            for (const item of items) {
                store.put(item)
            }

            tx.oncomplete = () => resolve()
            tx.onerror = () => reject(tx.error)
            tx.onabort = () => reject(tx.error)
        })
    }
    async getJobStatuses(): Promise<JobStatusCompact[]> {
        const db = await this.getDb()

        return await new Promise<JobStatusCompact[]>((resolve, reject) => {
            const tx = db.transaction(this.jobStatuses, "readonly")
            const store = tx.objectStore(this.jobStatuses)
            const request = store.getAll()

            request.onsuccess = () => resolve((request.result || []) as JobStatusCompact[])
            request.onerror = () => reject(request.error)
        })
    }

    // job types
    async updateJobTypes(items: JobTypeCompact[]): Promise<void> {
        if (!items || items.length === 0) return

        const db = await this.getDb()

        await new Promise<void>((resolve, reject) => {

            const tx = db.transaction(this.jobTypes, "readwrite")
            const store = tx.objectStore(this.jobTypes)

            store.clear()

            for (const item of items) {
                store.put(item)
            }

            tx.oncomplete = () => resolve()
            tx.onerror = () => reject(tx.error)
            tx.onabort = () => reject(tx.error)
        })
    }
    async getJobTypes(): Promise<JobTypeCompact[]> {
        const db = await this.getDb()

        return await new Promise<JobTypeCompact[]>((resolve, reject) => {
            const tx = db.transaction(this.jobTypes, "readonly")
            const store = tx.objectStore(this.jobTypes)
            const request = store.getAll()

            request.onsuccess = () => resolve((request.result || []) as JobTypeCompact[])
            request.onerror = () => reject(request.error)
        })
    }

    // job sources
    async updateJobSources(items: JobSourceCompact[]): Promise<void> {
        if (!items || items.length === 0) return
        const db = await this.getDb()
        await new Promise<void>((resolve, reject) => {

            const tx = db.transaction(this.jobSources, "readwrite")
            const store = tx.objectStore(this.jobSources)

            store.clear()

            for (const item of items) {
                store.put(item)
            }

            tx.oncomplete = () => resolve()
            tx.onerror = () => reject(tx.error)
            tx.onabort = () => reject(tx.error)
        })
    }
    async getJobSrouces(): Promise<JobSourceCompact[]> {
        const db = await this.getDb()

        return await new Promise<JobSourceCompact[]>((resolve, reject) => {
            const tx = db.transaction(this.jobSources, "readonly")
            const store = tx.objectStore(this.jobSources)
            const request = store.getAll()

            request.onsuccess = () => resolve((request.result || []) as JobSourceCompact[])
            request.onerror = () => reject(request.error)
        })
    }

    // job metros
    async updateJobMetros(items: JobMetroCompact[]): Promise<void> {
        if (!items || items.length === 0) return
        const db = await this.getDb()
        await new Promise<void>((resolve, reject) => {

            const tx = db.transaction(this.jobMetros, "readwrite")
            const store = tx.objectStore(this.jobMetros)

            store.clear()

            for (const item of items) {
                store.put(item)
            }

            tx.oncomplete = () => resolve()
            tx.onerror = () => reject(tx.error)
            tx.onabort = () => reject(tx.error)
        })
    }
    async getJobMetros(): Promise<JobMetroCompact[]> {
        const db = await this.getDb()

        return await new Promise<JobMetroCompact[]>((resolve, reject) => {
            const tx = db.transaction(this.jobMetros, "readonly")
            const store = tx.objectStore(this.jobMetros)
            const request = store.getAll()

            request.onsuccess = () => resolve((request.result || []) as JobMetroCompact[])
            request.onerror = () => reject(request.error)
        })
    }

    // techs
    async updateTechs(items: Technician[]): Promise<void> {
        if (!items || items.length === 0) return
        const db = await this.getDb()
        await new Promise<void>((resolve, reject) => {

            const tx = db.transaction(this.technicians, "readwrite")
            const store = tx.objectStore(this.technicians)

            store.clear()

            for (const item of items) {
                store.put(item)
            }

            tx.oncomplete = () => resolve()
            tx.onerror = () => reject(tx.error)
            tx.onabort = () => reject(tx.error)
        })
    }
    async getTechs(): Promise<Technician[]> {
        const db = await this.getDb()

        return await new Promise<Technician[]>((resolve, reject) => {
            const tx = db.transaction(this.technicians, "readonly")
            const store = tx.objectStore(this.technicians)
            const request = store.getAll()

            request.onsuccess = () => resolve((request.result || []) as Technician[])
            request.onerror = () => reject(request.error)
        })
    }

    // tags
    async updateTags(items: TagCompact[]): Promise<void> {
        if (!items || items.length === 0) return
        const db = await this.getDb()
        await new Promise<void>((resolve, reject) => {

            const tx = db.transaction(this.tags, "readwrite")
            const store = tx.objectStore(this.tags)

            store.clear()

            for (const item of items) {
                store.put(item)
            }

            tx.oncomplete = () => resolve()
            tx.onerror = () => reject(tx.error)
            tx.onabort = () => reject(tx.error)
        })
    }
    async getTags(): Promise<TagCompact[]> {
        const db = await this.getDb()

        return await new Promise<TagCompact[]>((resolve, reject) => {
            const tx = db.transaction(this.tags, "readonly")
            const store = tx.objectStore(this.tags)
            const request = store.getAll()

            request.onsuccess = () => resolve((request.result || []) as TagCompact[])
            request.onerror = () => reject(request.error)
        })
    }

    // flags
    async updateFlags(items: FlagCompact[]): Promise<void> {
        if (!items || items.length === 0) return
        const db = await this.getDb()
        await new Promise<void>((resolve, reject) => {

            const tx = db.transaction(this.flags, "readwrite")
            const store = tx.objectStore(this.flags)

            store.clear()

            for (const item of items) {
                store.put(item)
            }

            tx.oncomplete = () => resolve()
            tx.onerror = () => reject(tx.error)
            tx.onabort = () => reject(tx.error)
        })
    }
    async getFlags(): Promise<FlagCompact[]> {
        const db = await this.getDb()

        return await new Promise<FlagCompact[]>((resolve, reject) => {
            const tx = db.transaction(this.flags, "readonly")
            const store = tx.objectStore(this.flags)
            const request = store.getAll()

            request.onsuccess = () => resolve((request.result || []) as FlagCompact[])
            request.onerror = () => reject(request.error)
        })
    }

    // announcements
    async updateAnnouncements(items: Announcement[]): Promise<void> {
        if (!items || items.length === 0) return
        const db = await this.getDb()
        await new Promise<void>((resolve, reject) => {

            const tx = db.transaction(this.announcements, "readwrite")
            const store = tx.objectStore(this.announcements)

            store.clear()

            for (const item of items) {
                store.put(item)
            }

            tx.oncomplete = () => resolve()
            tx.onerror = () => reject(tx.error)
            tx.onabort = () => reject(tx.error)
        })
    }

    async getAnnouncements(): Promise<Announcement[]> {
        const db = await this.getDb()

        return await new Promise<Announcement[]>((resolve, reject) => {
            const tx = db.transaction(this.announcements, "readonly")
            const store = tx.objectStore(this.announcements)
            const request = store.getAll()

            request.onsuccess = () => resolve((request.result || []) as Announcement[])
            request.onerror = () => reject(request.error)
        })
    }

    // quick replies
    async updateQuickReplies(items: ChatQuickReplyItem[]): Promise<void> {
        if (!items || items.length === 0) return
        const db = await this.getDb()
        await new Promise<void>((resolve, reject) => {

            const tx = db.transaction(this.quickReplies, "readwrite")
            const store = tx.objectStore(this.quickReplies)

            store.clear()

            for (const item of items) {
                store.put(item)
            }

            tx.oncomplete = () => resolve()
            tx.onerror = () => reject(tx.error)
            tx.onabort = () => reject(tx.error)
        })
    }

    async getQuickReplies(): Promise<ChatQuickReplyItem[]> {
        const db = await this.getDb()

        return await new Promise<ChatQuickReplyItem[]>((resolve, reject) => {
            const tx = db.transaction(this.quickReplies, "readonly")
            const store = tx.objectStore(this.quickReplies)
            const request = store.getAll()

            request.onsuccess = () => resolve((request.result || []) as ChatQuickReplyItem[])
            request.onerror = () => reject(request.error)
        })
    }

    // countries
    async updateCountries(items: Country[]): Promise<void> {
        if (!items || items.length === 0) return
        const db = await this.getDb()
        await new Promise<void>((resolve, reject) => {

            const tx = db.transaction(this.countries, "readwrite")
            const store = tx.objectStore(this.countries)

            store.clear()

            for (const item of items) {
                store.put(item)
            }

            tx.oncomplete = () => resolve()
            tx.onerror = () => reject(tx.error)
            tx.onabort = () => reject(tx.error)
        })
    }

    async getCountries(): Promise<Country[]> {
        const db = await this.getDb()

        return await new Promise<Country[]>((resolve, reject) => {
            const tx = db.transaction(this.countries, "readonly")
            const store = tx.objectStore(this.countries)
            const request = store.getAll()

            request.onsuccess = () => resolve((request.result || []) as Country[])
            request.onerror = () => reject(request.error)
        })
    }

    async updateCountriesAll(items: Country[]): Promise<void> {
        if (!items || items.length === 0) return
        const db = await this.getDb()
        await new Promise<void>((resolve, reject) => {

            const tx = db.transaction(this.countriesAll, "readwrite")
            const store = tx.objectStore(this.countriesAll)

            store.clear()

            for (const item of items) {
                store.put(item)
            }

            tx.oncomplete = () => resolve()
            tx.onerror = () => reject(tx.error)
            tx.onabort = () => reject(tx.error)
        })
    }

    async getCountriesAll(): Promise<Country[]> {
        const db = await this.getDb()

        return await new Promise<Country[]>((resolve, reject) => {
            const tx = db.transaction(this.countriesAll, "readonly")
            const store = tx.objectStore(this.countriesAll)
            const request = store.getAll()

            request.onsuccess = () => resolve((request.result || []) as Country[])
            request.onerror = () => reject(request.error)
        })
    }



    // extensions
    async updateExtensions(items: ExtensionCompact[]): Promise<void> {
        if (!items || items.length === 0) return
        const db = await this.getDb()
        await new Promise<void>((resolve, reject) => {

            const tx = db.transaction(this.extensions, "readwrite")
            const store = tx.objectStore(this.extensions)

            store.clear()

            for (const item of items) {
                store.put(item)
            }

            tx.oncomplete = () => resolve()
            tx.onerror = () => reject(tx.error)
            tx.onabort = () => reject(tx.error)
        })
    }

    async getExtensions(): Promise<ExtensionCompact[]> {
        const db = await this.getDb()

        return await new Promise<ExtensionCompact[]>((resolve, reject) => {
            const tx = db.transaction(this.extensions, "readonly")
            const store = tx.objectStore(this.extensions)
            const request = store.getAll()

            request.onsuccess = () => resolve((request.result || []) as ExtensionCompact[])
            request.onerror = () => reject(request.error)
        })
    }

    // queues
    async updateQueues(items: QueueCompact[]): Promise<void> {
        if (!items || items.length === 0) return
        const db = await this.getDb()
        await new Promise<void>((resolve, reject) => {

            const tx = db.transaction(this.queues, "readwrite")
            const store = tx.objectStore(this.queues)

            store.clear()

            for (const item of items) {
                store.put(item)
            }

            tx.oncomplete = () => resolve()
            tx.onerror = () => reject(tx.error)
            tx.onabort = () => reject(tx.error)
        })
    }

    async getQueues(): Promise<QueueCompact[]> {
        const db = await this.getDb()

        return await new Promise<QueueCompact[]>((resolve, reject) => {
            const tx = db.transaction(this.queues, "readonly")
            const store = tx.objectStore(this.queues)
            const request = store.getAll()

            request.onsuccess = () => resolve((request.result || []) as QueueCompact[])
            request.onerror = () => reject(request.error)
        })
    }

    // phone lines
    async updatePhoneLines(items: PhoneLineCompact[]): Promise<void> {
        const db = await this.getDb()
        await new Promise<void>((resolve, reject) => {
            const tx = db.transaction(this.phoneLines, "readwrite")
            const store = tx.objectStore(this.phoneLines)

            store.clear()

            for (const item of items ?? []) {
                store.put(item)
            }

            tx.oncomplete = () => resolve()
            tx.onerror = () => reject(tx.error)
            tx.onabort = () => reject(tx.error)
        })
    }

    async getPhoneLines(): Promise<PhoneLineCompact[]> {
        const db = await this.getDb()

        return await new Promise<PhoneLineCompact[]>((resolve, reject) => {
            const tx = db.transaction(this.phoneLines, "readonly")
            const store = tx.objectStore(this.phoneLines)
            const request = store.getAll()

            request.onsuccess = () => resolve((request.result || []) as PhoneLineCompact[])
            request.onerror = () => reject(request.error)
        })
    }


}


/**
 * Minimal in-memory IDBDatabase stand-in. Used when Chrome (or any browser)
 * can't open the real IndexedDB backing store. Keeps the rest of the app
 * working without persistence — data is just refetched from the server.
 */
function createInMemoryDb(): IDBDatabase {
    const stores = new Map<string, Map<any, any>>()

    const ensureStore = (name: string) => {
        let s = stores.get(name)
        if (!s) { s = new Map(); stores.set(name, s) }
        return s
    }

    const fireSuccess = (req: any, result: any) => {
        Promise.resolve().then(() => {
            req.result = result
            try { req.onsuccess?.({ target: req }) } catch { }
        })
    }

    const createStore = (name: string): IDBObjectStore => {
        const store = ensureStore(name)
        return {
            clear() {
                const req: any = {}
                store.clear()
                fireSuccess(req, undefined)
                return req
            },
            put(item: any) {
                const req: any = {}
                const key = item?.id ?? item?.key ?? Math.random().toString(36).slice(2)
                store.set(key, item)
                fireSuccess(req, key)
                return req
            },
            get(key: any) {
                const req: any = {}
                fireSuccess(req, store.get(key))
                return req
            },
            getAll() {
                const req: any = {}
                fireSuccess(req, Array.from(store.values()))
                return req
            },
            createIndex() { return {} as any },
        } as any
    }

    return {
        transaction(storeNames: any, _mode?: any) {
            const tx: any = {
                objectStore: (n: string) => createStore(n),
                oncomplete: null,
                onerror: null,
                onabort: null,
                error: null,
            }
            Promise.resolve().then(() => {
                try { tx.oncomplete?.() } catch { }
            })
            return tx
        },
        objectStoreNames: {
            contains: () => true,
            length: 0,
            item: () => null,
            [Symbol.iterator]: function* () { },
        },
        createObjectStore: (name: string) => createStore(name),
        close: () => { },
        onversionchange: null,
        onclose: null,
        name: "rinvox-db",
        version: 0,
    } as any
}